import React, { useEffect, useState } from 'react';
import {
    Form,
    Input,
    Button,
    Spin,
    Switch,
    InputNumber,
    Segmented,
} from 'antd';
import {DatePicker} from "antd-jalali";
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import AdminLayout from "@/components/layout/admin/adminLayout";
import { URLS } from "@/lib/urls";
import ApiHandleSuccessMessage, {
    ApiHandleErrorMessage
} from "@/lib/helper/apiHandleResultMessage";
import { gradeApi } from "@/api/grade";

const { TextArea } = Input;

const toDateValue = (value) => {
    if (!value) return null;

    if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        return dayjs.unix(Number(value));
    }

    const date = dayjs(value);
    return date.isValid() ? date : null;
};

function AdminGradeEditPage() {
    const { id } = useParams();

    const [form] = Form.useForm();

    const [limitType, setLimitType] = useState('age');

    const { data: grade, isLoading } = gradeApi.useAdminGetGrade({ id });

    const { mutate, isPending } = gradeApi.useAdminUpdateGrade();

    useEffect(() => {
        if (grade) {
            const item = grade?.data?.data ?? grade;

            const hasDateLimit = item.minagedate || item.maxagedate;

            setLimitType(hasDateLimit ? 'date' : 'age');

            form.setFieldsValue({
                title: item.title,
                desc: item.desc,
                guide: item.guide,

                minage: item.minage ?? null,
                maxage: item.maxage ?? null,

                minagedate: toDateValue(item.minagedate),
                maxagedate: toDateValue(item.maxagedate),

                sort: item.sort ?? null,
                status: item.status,
                signup: item.signup,
            });
        }
    }, [grade, form]);

    const handleSubmit = (values) => {
        const payload = {
            id: Number(id),
            ...values,
            age_limit: limitType,

            minagedate: limitType === 'date'
                ? values.minagedate?.unix() ?? null
                : null,

            maxagedate: limitType === 'date'
                ? values.maxagedate?.unix() ?? null
                : null,

            minage: limitType === 'age'
                ? values.minage ?? null
                : null,

            maxage: limitType === 'age'
                ? values.maxage ?? null
                : null,
        };

        mutate(
            payload,
            {
                onSuccess: (res) => {
                    ApiHandleSuccessMessage(res);
                },
                onError: (err) => {
                    ApiHandleErrorMessage(err);
                }
            }
        );
    };

    if (isLoading) {
        return (
            <AdminLayout
                pageTitle="ویرایش مقطع تحصیلی"
                backTo={URLS.admin.grade.list}
            >
                <div className="flex min-h-64 items-center justify-center">
                    <Spin size="large" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout
            pageTitle={"ویرایش مقطع تحصیلی" + ' #' + grade?.data?.data?.id}
            backTo={URLS.admin.grade.list}
        >
            <div className="w-full p-4 sm:p-6">
                <div className="mx-auto w-full max-w-3xl rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm sm:p-6 md:p-8">

                    <div className="mb-6">
                        <h2 className="text-lg font-bold sm:text-xl">
                            ویرایش اطلاعات مقطع تحصیلی
                        </h2>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        autoComplete="off"
                    >
                        <Form.Item
                            label="عنوان مقطع تحصیلی"
                            name="title"
                        >
                            <Input
                                size="middle"
                                placeholder="عنوان مقطع تحصیلی"
                                className="!rounded-lg"
                            />
                        </Form.Item>

                        <Form.Item
                            label="توضیحات"
                            name="desc"
                        >
                            <TextArea
                                rows={4}
                                placeholder="توضیحات مقطع تحصیلی..."
                                className="!rounded-lg"
                            />
                        </Form.Item>

                        <Form.Item
                            label="راهنما"
                            name="guide"
                        >
                            <TextArea
                                rows={4}
                                placeholder="راهنمای مربوط به این مقطع..."
                                className="!rounded-lg"
                            />
                        </Form.Item>

                        <div className="mb-5">

                            <div className="mb-4">
                                <h3 className="text-base font-bold">
                                    محدودیت سنی
                                </h3>
                            </div>

                            <Segmented
                                block
                                size="large"
                                value={limitType}
                                onChange={setLimitType}
                                options={[
                                    {
                                        label: 'محدودیت با سن',
                                        value: 'age',
                                    },
                                    {
                                        label: 'محدودیت با تاریخ تولد',
                                        value: 'date',
                                    },
                                ]}
                            />

                            <div className="mt-5">

                                {limitType === 'age' ? (
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                                        <Form.Item
                                            label="حداقل سن"
                                            name="minage"
                                        >
                                            <InputNumber
                                                min={0}
                                                max={100}
                                                size="middle"
                                                placeholder="حداقل سن"
                                                addonAfter="سال"
                                                className="!w-full !rounded-lg"
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="حداکثر سن"
                                            name="maxage"
                                        >
                                            <InputNumber
                                                min={0}
                                                max={100}
                                                size="middle"
                                                placeholder="حداکثر سن"
                                                addonAfter="سال"
                                                className="!w-full !rounded-lg"
                                            />
                                        </Form.Item>

                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                                        <Form.Item
                                            label="از تاریخ تولد"
                                            name="minagedate"
                                            getValueProps={(value) => ({
                                                value: value ? dayjs(value).calendar("jalali") : null,
                                            })}
                                            getValueFromEvent={(date) => (
                                                date ? date.calendar("gregory") : null
                                            )}
                                        >
                                            <DatePicker
                                                className="!w-full"
                                                format="YYYY/MM/DD"
                                                placeholder="انتخاب تاریخ شروع"
                                                allowClear
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="تا تاریخ تولد"
                                            name="maxagedate"
                                            getValueProps={(value) => ({
                                                value: value ? dayjs(value).calendar("jalali") : null,
                                            })}
                                            getValueFromEvent={(date) => (
                                                date ? date.calendar("gregory") : null
                                            )}
                                        >
                                            <DatePicker
                                                className="!w-full"
                                                format="YYYY/MM/DD"
                                                placeholder="انتخاب تاریخ پایان"
                                                allowClear
                                            />
                                        </Form.Item>

                                    </div>
                                )}

                            </div>
                        </div>

                        <Form.Item
                            label="ترتیب نمایش"
                            name="sort"
                        >
                            <InputNumber
                                min={0}
                                size="middle"
                                placeholder="ترتیب نمایش"
                                className="!w-full !rounded-lg"
                            />
                        </Form.Item>

                        <Form.Item
                            label="وضعیت مقطع"
                            name="status"
                            valuePropName="checked"
                            getValueProps={(value) => ({
                                checked: value === 'enable',
                            })}
                            getValueFromEvent={(checked) => (
                                checked ? 'enable' : 'disable'
                            )}
                        >
                            <Switch
                                checkedChildren="فعال"
                                unCheckedChildren="غیرفعال"
                            />
                        </Form.Item>

                        <Form.Item
                            label="وضعیت ثبت‌نام"
                            name="signup"
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren="باز"
                                unCheckedChildren="بسته"
                            />
                        </Form.Item>

                        <div className="flex flex-col-reverse gap-3 border-t dark:border-gray-700 pt-5 sm:flex-row sm:justify-end">
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="middle"
                                loading={isPending}
                                className="!rounded-lg"
                            >
                                ذخیره تغییرات
                            </Button>
                        </div>

                    </Form>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminGradeEditPage;